/**
 * GCenteredPillTabs — 居中胶囊 Tab 导航（指示条 + 移动端居中滚动 + 左右 paddle）
 *
 * 用法概要（详见 README.md「共享资源：g-centered-pill-tabs.js」）：
 * 1. Markup 根节点加 data-g-centered-pill-tabs，并包含 viewport / track / tab /
 *    indicator / previous / next 等 data-g-centered-pill-tabs-* 钩子。
 * 2. CSS 用 --g-centered-pill-tabs-indicator-start|width 驱动指示条；可选设置
 *    --g-centered-pill-tabs-duration、--g-centered-pill-tabs-padding。
 * 3. Section 内：
 *      <script src="{{ 'g-centered-pill-tabs.js' | asset_url }}" defer></script>
 *      const tabs = new window.GCenteredPillTabs(root, { initialIndex, mobileQuery });
 *    可监听 g:centered-pill-tabs:request（或 GCenteredPillTabs.requestEvent）拦截切换；
 *    脚本未就绪时等 g:centered-pill-tabs:ready。卸载时调用 tabs.destroy()。
 *
 * 当前消费者：sections/g-carousel-tab-preview.liquid
 */
(() => {
  const READY_EVENT = 'g:centered-pill-tabs:ready';
  const REQUEST_EVENT = 'g:centered-pill-tabs:request';

  if (window.GCenteredPillTabs) {
    document.dispatchEvent(new CustomEvent(READY_EVENT));
    return;
  }

  const createCubicBezier = (x1, y1, x2, y2) => {
    const sample = (a1, a2, value) => {
      const coefficientA = 1 - 3 * a2 + 3 * a1;
      const coefficientB = 3 * a2 - 6 * a1;
      const coefficientC = 3 * a1;
      return ((coefficientA * value + coefficientB) * value + coefficientC) * value;
    };
    const slope = (a1, a2, value) => {
      const coefficientA = 1 - 3 * a2 + 3 * a1;
      const coefficientB = 3 * a2 - 6 * a1;
      const coefficientC = 3 * a1;
      return 3 * coefficientA * value * value + 2 * coefficientB * value + coefficientC;
    };

    return (progress) => {
      if (progress <= 0 || progress >= 1) return progress;

      let parameter = progress;
      for (let iteration = 0; iteration < 6; iteration += 1) {
        const currentSlope = slope(x1, x2, parameter);
        if (Math.abs(currentSlope) < 0.000001) break;
        parameter -= (sample(x1, x2, parameter) - progress) / currentSlope;
      }

      if (parameter < 0 || parameter > 1) {
        let lower = 0;
        let upper = 1;
        parameter = progress;
        for (let iteration = 0; iteration < 12; iteration += 1) {
          const current = sample(x1, x2, parameter);
          if (Math.abs(current - progress) < 0.000001) break;
          if (current < progress) {
            lower = parameter;
          } else {
            upper = parameter;
          }
          parameter = (lower + upper) / 2;
        }
      }

      return sample(y1, y2, parameter);
    };
  };

  const easeOut = createCubicBezier(0, 0, 0.58, 1);

  class GCenteredPillTabs {
    static requestEvent = REQUEST_EVENT;

    constructor(root, options = {}) {
      if (!(root instanceof HTMLElement)) {
        throw new TypeError('GCenteredPillTabs requires a root element.');
      }

      if (root.gCenteredPillTabs instanceof GCenteredPillTabs) {
        root.gCenteredPillTabs.destroy();
      }

      this.root = root;
      this.viewport = root.querySelector('[data-g-centered-pill-tabs-viewport]');
      this.track = root.querySelector('[data-g-centered-pill-tabs-track]');
      this.indicator = root.querySelector('[data-g-centered-pill-tabs-indicator]');
      this.tabs = Array.from(root.querySelectorAll('[data-g-centered-pill-tabs-tab]'));
      this.previousButton = root.querySelector('[data-g-centered-pill-tabs-previous]');
      this.nextButton = root.querySelector('[data-g-centered-pill-tabs-next]');
      this.mobileQuery =
        options.mobileQuery instanceof MediaQueryList
          ? options.mobileQuery
          : window.matchMedia(options.mobileQuery || '(max-width: 768px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = Math.max(
        0,
        Math.min(Number(options.initialIndex) || 0, this.tabs.length - 1)
      );
      this.animationFrame = 0;
      this.cleanups = [];
      this.destroyed = false;
      this.ready = false;

      if (
        !this.viewport ||
        !this.track ||
        !this.indicator ||
        !this.tabs.length ||
        !this.previousButton ||
        !this.nextButton
      ) {
        throw new Error('GCenteredPillTabs is missing required data hooks.');
      }

      this.addListener(this.previousButton, 'click', (event) => {
        this.requestIndex(this.activeIndex - 1, event);
      });
      this.addListener(this.nextButton, 'click', (event) => {
        this.requestIndex(this.activeIndex + 1, event);
      });

      this.tabs.forEach((tab, index) => {
        this.addListener(tab, 'click', (event) => {
          this.requestIndex(index, event);
        });
        this.addListener(tab, 'keydown', (event) => {
          this.handleKeydown(event, index);
        });
      });

      const onResponsiveChange = () => this.refresh({ animate: false });
      this.addListener(this.mobileQuery, 'change', onResponsiveChange);
      this.addListener(this.reduceMotionQuery, 'change', onResponsiveChange);

      this.resizeObserver = new ResizeObserver(() => this.refresh({ animate: false }));
      this.resizeObserver.observe(this.root);
      this.resizeObserver.observe(this.track);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (!this.destroyed) this.refresh({ animate: false });
        });
      }

      root.gCenteredPillTabs = this;
      this.setActive(this.activeIndex, { animate: false });
    }

    addListener(target, eventName, handler, options) {
      target.addEventListener(eventName, handler, options);
      this.cleanups.push(() => target.removeEventListener(eventName, handler, options));
    }

    requestIndex(index, sourceEvent) {
      const targetIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
      if (targetIndex === this.activeIndex) return false;

      const request = new CustomEvent(REQUEST_EVENT, {
        bubbles: true,
        cancelable: true,
        detail: {
          index: targetIndex,
          accepted: true,
          sourceEvent
        }
      });
      const shouldSelectImmediately = this.root.dispatchEvent(request);
      if (shouldSelectImmediately) this.setActive(targetIndex);
      return shouldSelectImmediately || request.detail.accepted !== false;
    }

    handleKeydown(event, focusedIndex) {
      let targetIndex = focusedIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        targetIndex = (focusedIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        targetIndex = (focusedIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        targetIndex = 0;
      } else if (event.key === 'End') {
        targetIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      if (targetIndex !== focusedIndex) {
        this.requestIndex(targetIndex, event);
        this.tabs[targetIndex].focus({ preventScroll: true });
      }
    }

    setActive(index, options = {}) {
      if (this.destroyed || !this.tabs[index]) return false;
      if (this.ready && index === this.activeIndex && options.force !== true) return false;

      const animate = options.animate !== false && this.ready && !this.reduceMotionQuery.matches;
      this.activeIndex = index;
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });

      this.layoutActiveTab({ animate });
      if (!this.ready) {
        this.indicator.getBoundingClientRect();
        this.root.dataset.gCenteredPillTabsReady = 'true';
        this.ready = true;
      }
      return true;
    }

    refresh(options = {}) {
      if (this.destroyed) return;
      this.layoutActiveTab({ animate: options.animate === true && !this.reduceMotionQuery.matches });
    }

    getDuration() {
      const value = window
        .getComputedStyle(this.root)
        .getPropertyValue('--g-centered-pill-tabs-duration')
        .trim();
      if (!value) return 320;
      if (value.endsWith('ms')) return Number.parseFloat(value) || 320;
      if (value.endsWith('s')) return (Number.parseFloat(value) || 0.32) * 1000;
      return Number.parseFloat(value) || 320;
    }

    getPadding() {
      const value = window
        .getComputedStyle(this.root)
        .getPropertyValue('--g-centered-pill-tabs-padding');
      return Number.parseFloat(value) || 4;
    }

    getMobileLayout(tab) {
      const itemLeft = tab.offsetLeft;
      const itemWidth = tab.offsetWidth;
      const itemsWidth = this.track.scrollWidth;
      const platterWidth = this.root.offsetWidth;
      const platterPadding = this.getPadding();
      const platterCenter = platterWidth / 2;
      const itemCenter = itemWidth / 2;
      const overflowing = itemsWidth + 2 * platterPadding - 1 > platterWidth;
      let position = 'center';
      let indicatorStart;
      let scrollLeft;

      if (itemLeft + itemCenter > itemsWidth - platterCenter) {
        position = 'right';
        indicatorStart = itemLeft + (platterWidth - itemsWidth) - platterPadding;
        scrollLeft = itemsWidth - platterWidth + 2 * platterPadding;
      } else if (itemLeft + itemCenter < platterCenter) {
        position = 'left';
        indicatorStart = itemLeft + platterPadding;
        scrollLeft = 0;
      } else {
        indicatorStart = platterCenter - itemCenter;
        scrollLeft = itemLeft - (platterCenter - itemCenter) + platterPadding;
      }

      return {
        indicatorStart,
        indicatorWidth: itemWidth,
        scrollLeft: Math.max(0, scrollLeft),
        showPrevious: overflowing && position !== 'left' && this.activeIndex > 0,
        showNext:
          overflowing && position !== 'right' && this.activeIndex < this.tabs.length - 1
      };
    }

    getDesktopLayout(tab) {
      return {
        indicatorStart: tab.offsetLeft,
        indicatorWidth: tab.offsetWidth,
        scrollLeft: 0,
        showPrevious: false,
        showNext: false
      };
    }

    layoutActiveTab({ animate }) {
      const tab = this.tabs[this.activeIndex];
      if (!tab) return;

      const layout = this.mobileQuery.matches
        ? this.getMobileLayout(tab)
        : this.getDesktopLayout(tab);
      this.root.toggleAttribute('data-g-centered-pill-tabs-instant', !animate);
      this.root.style.setProperty(
        '--g-centered-pill-tabs-indicator-start',
        `${Math.round(layout.indicatorStart)}px`
      );
      this.root.style.setProperty(
        '--g-centered-pill-tabs-indicator-width',
        `${Math.round(layout.indicatorWidth)}px`
      );
      this.updatePaddles(layout.showPrevious, layout.showNext);
      this.animateScroll(layout.scrollLeft, animate);

      if (!animate) {
        this.indicator.getBoundingClientRect();
        this.root.removeAttribute('data-g-centered-pill-tabs-instant');
      }
    }

    updatePaddles(showPrevious, showNext) {
      this.root.classList.toggle('has-scroll-left', showPrevious);
      this.root.classList.toggle('has-scroll-right', showNext);

      [
        [this.previousButton, showPrevious],
        [this.nextButton, showNext]
      ].forEach(([button, enabled]) => {
        button.disabled = !enabled;
        button.tabIndex = enabled ? 0 : -1;
        button.setAttribute('aria-hidden', enabled ? 'false' : 'true');
      });
    }

    animateScroll(target, animate) {
      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = 0;
      }

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetScroll = Math.max(0, Math.min(target, maxScroll));
      const startScroll = this.viewport.scrollLeft;
      const distance = targetScroll - startScroll;

      if (!animate || Math.abs(distance) < 1) {
        this.viewport.scrollLeft = targetScroll;
        return;
      }

      const duration = this.getDuration();
      const startedAt = performance.now();
      const draw = (timestamp) => {
        const progress = Math.min(1, (timestamp - startedAt) / duration);
        const nextScroll = startScroll + distance * easeOut(progress);
        this.viewport.scrollLeft = distance < 0 ? Math.floor(nextScroll) : Math.ceil(nextScroll);

        if (progress < 1) {
          this.animationFrame = window.requestAnimationFrame(draw);
        } else {
          this.viewport.scrollLeft = targetScroll;
          this.animationFrame = 0;
        }
      };

      this.animationFrame = window.requestAnimationFrame(draw);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.resizeObserver.disconnect();
      this.cleanups.forEach((cleanup) => cleanup());
      this.root.classList.remove('has-scroll-left', 'has-scroll-right');
      this.root.removeAttribute('data-g-centered-pill-tabs-ready');
      this.root.removeAttribute('data-g-centered-pill-tabs-instant');
      this.root.style.removeProperty('--g-centered-pill-tabs-indicator-start');
      this.root.style.removeProperty('--g-centered-pill-tabs-indicator-width');
      if (this.root.gCenteredPillTabs === this) delete this.root.gCenteredPillTabs;
    }
  }

  window.GCenteredPillTabs = GCenteredPillTabs;
  document.dispatchEvent(new CustomEvent(READY_EVENT));
})();
